import { useState } from 'react';
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { AiDisclosureModal } from '../../../components/session/AiDisclosureModal';
import { coachActiveSessionApi } from '../../../services/api/coachActiveSession';

interface StartSessionButtonProps {
  sessionId: string;
  cohortId: string;
  scheduledStart: string | null;
  sessionStatus: string;
  groupName: string | null;
  aiFeaturesEnabled: boolean;
}

/** Number of minutes before scheduled_start when the button becomes active (AC14 on MED-814 + AC1 on MED-816). */
const READY_TO_START_WINDOW_MIN = 15;

function isInReadyWindow(scheduledStart: string | null): boolean {
  if (!scheduledStart) return false;
  const start = new Date(scheduledStart).getTime();
  if (Number.isNaN(start)) return false;
  const now = Date.now();
  return (
    now >= start - READY_TO_START_WINDOW_MIN * 60_000 &&
    now < start + 60 * 60_000
  );
}

/**
 * MED-816 / E4.4 — Peer-leader Start Session button.
 *
 * Renders inside the prep-page header when the session is `scheduled` AND
 * we're inside the T-15 ready-to-start window (per MED-814 AC14). Clicking
 * follows the same mechanic as the coach launch path in MED-792:
 *
 *   1. If the cohort's group_coaching modality has AI features enabled, show
 *      the AI disclosure modal first (MED-816 AC12 — peer leader uses the
 *      `coach` role variant whose copy reads "What the session leader sees").
 *   2. On acknowledge → `POST /api/coach/sessions/:id/start`. The endpoint
 *      accepts the peer leader because `CoachActiveSessionService.startSession`
 *      now calls `assertSessionLeaderOrCoach`.
 *   3. Navigate to `/participant/cohorts/:cohortId/sessions/:sessionId/active`
 *      (the peer-leader URL — MED-816 AC2 + AC14). The same
 *      `CoachActiveSessionPage` component handles both URLs; it detects the
 *      `/participant/` prefix and reroutes Back / End Session / AI-dismiss
 *      to participant targets.
 *   4. On AI-modal dismiss (X / Escape) → close modal, stay on prep. No
 *      session-start fires.
 *
 * This replaces the "coming soon" stub MED-814 originally shipped.
 */
export function StartSessionButton({
  sessionId,
  cohortId,
  scheduledStart,
  sessionStatus,
  groupName,
  aiFeaturesEnabled,
}: StartSessionButtonProps) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);

  // MED-1045 (AC3): match the coach Start button pattern
  // (CoachPrepGroupSection.tsx — `disabled={group.status_label !== 'ready_to_start'}`).
  // The button is only relevant for a `scheduled` session, but inside that
  // state it must always *render*: gray/disabled before the T-15 ready window
  // and active within it, rather than being hidden until T-15. Previously this
  // did `if (!isInReadyWindow(...)) return null;`, which diverged from the coach.
  if (sessionStatus !== 'scheduled') return null;

  const isReadyToStart = isInReadyWindow(scheduledStart);

  const label = groupName ? `Start Session — ${groupName}` : 'Start Session';
  const activeUrl = `/participant/cohorts/${cohortId}/sessions/${sessionId}/active`;

  const launchSession = async () => {
    setSubmitting(true);
    try {
      await coachActiveSessionApi.startSession(sessionId);
      navigate(activeUrl);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: "Couldn't start session",
        message: err instanceof Error ? err.message : 'Please try again.',
      });
      setSubmitting(false);
    }
  };

  const handleClick = () => {
    if (aiFeaturesEnabled) {
      setAiModalOpen(true);
      return;
    }
    void launchSession();
  };

  const handleAcknowledge = () => {
    setAiModalOpen(false);
    // Persist the per-session ack BEFORE navigating so `CoachActiveSessionPage`
    // (which reads the same `medley.activeSession.aiAck.<sessionId>` key on
    // mount) does not double-prompt the peer leader. Coach launch path in
    // MED-792 / MED-793 uses the same key — this mirrors that behavior so
    // both leader roles see a single disclosure per session.
    try {
      window.localStorage.setItem(
        `medley.activeSession.aiAck.${sessionId}`,
        '1'
      );
    } catch {
      // localStorage may be unavailable in some private-browsing contexts;
      // a missing ack just means the active page will re-show the modal once,
      // which is a soft degradation, not a blocker.
    }
    void launchSession();
  };

  const handleDismiss = () => {
    setAiModalOpen(false);
    // Per MED-816 AC12: dismissing the modal returns the leader to the prep
    // flow — already where they are, so no navigation needed.
  };

  return (
    <>
      <Button
        color="medleyGreen"
        size="sm"
        loading={submitting}
        disabled={!isReadyToStart}
        onClick={handleClick}
      >
        {label}
      </Button>
      <AiDisclosureModal
        opened={aiModalOpen}
        role="coach"
        onAcknowledge={handleAcknowledge}
        onDismiss={handleDismiss}
      />
    </>
  );
}
